import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PackageSearch, Check, Truck, Home, X, ChevronRight, Package } from "lucide-react";

const NEXT_STATUS = {
  placed: { key: "confirmed", label: "Mark Confirmed", Icon: Check },
  confirmed: { key: "shipped", label: "Mark Shipped", Icon: Truck },
  shipped: { key: "delivered", label: "Mark Delivered", Icon: Home },
  delivered: null,
  cancelled: null,
};

const STATUS_META = {
  placed: { label: "Placed", tone: "bg-secondary text-foreground" },
  confirmed: { label: "Confirmed", tone: "bg-electric/10 text-electric" },
  shipped: { label: "Shipped", tone: "bg-electric/20 text-electric" },
  delivered: { label: "Delivered", tone: "bg-green-500/15 text-green-600 dark:text-green-400" },
  cancelled: { label: "Cancelled", tone: "bg-destructive/15 text-destructive" },
};

export default function AdminOrdersPage() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { if (user?.role === "admin") load(); }, [user, statusFilter]);

  async function load() {
    const params = {};
    if (statusFilter !== "all") params.order_status = statusFilter;
    const { data } = await api.get("/admin/orders", { params });
    setOrders(data);
    if (selected) {
      const updated = data.find((o) => o.id === selected.id);
      if (updated) setSelected(updated);
    }
  }

  async function updateStatus(order, newStatus) {
    setBusyId(order.id);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/status`, { status: newStatus });
      toast.success(`Order ${order.order_number} → ${STATUS_META[newStatus].label}`);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? data : o)));
      if (selected?.id === order.id) setSelected(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <div className="p-12">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const stats = {
    all: orders.length,
    placed: orders.filter((o) => o.status === "placed").length,
    confirmed: orders.filter((o) => o.status === "confirmed").length,
    shipped: orders.filter((o) => o.status === "shipped").length,
    delivered: orders.filter((o) => o.status === "delivered").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <AdminTabs />
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Admin · Orders</div>
          <h1 className="mt-2 font-heading font-medium text-4xl">Order pipeline</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {[
          { k: "all", l: "All", n: stats.all },
          { k: "placed", l: "Placed", n: stats.placed },
          { k: "confirmed", l: "Confirmed", n: stats.confirmed },
          { k: "shipped", l: "Shipped", n: stats.shipped },
          { k: "delivered", l: "Delivered", n: stats.delivered },
        ].map((s) => (
          <button
            key={s.k}
            onClick={() => setStatusFilter(s.k)}
            data-testid={`filter-status-${s.k}`}
            className={`text-left p-4 rounded-2xl border transition-all ${
              statusFilter === s.k ? "border-electric bg-electric/5" : "border-border hover:border-foreground/40"
            }`}
          >
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{s.l}</div>
            <div className="mt-2 font-heading font-medium text-3xl tabular-nums">{s.n}</div>
          </button>
        ))}
      </div>

      {orders.length === 0 ? (
        <div className="border border-dashed border-border rounded-2xl p-12 text-center">
          <Package className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="mt-4 text-sm text-muted-foreground">No orders in this bucket.</div>
        </div>
      ) : (
        <div className="grid gap-2">
          {orders.map((o) => {
            const next = NEXT_STATUS[o.status];
            const meta = STATUS_META[o.status] || STATUS_META.placed;
            return (
              <div key={o.id} data-testid={`admin-order-row-${o.order_number}`} className="flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-xl border border-border hover:border-foreground/40 transition-colors">
                <button onClick={() => setSelected(o)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <div className="font-mono text-sm font-medium">{o.order_number}</div>
                    <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${meta.tone}`}>{meta.label}</span>
                    {o.payment_status === "paid" && <span className="text-[10px] font-mono uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400">PAID</span>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {o.email} · {o.items?.length || 0} items · {new Date(o.created_at).toLocaleString()}
                  </div>
                </button>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-medium tabular-nums whitespace-nowrap">₹{o.total?.toLocaleString()}</div>
                  {next && (
                    <button
                      onClick={() => updateStatus(o, next.key)}
                      disabled={busyId === o.id}
                      data-testid={`advance-${o.order_number}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-electric text-white text-xs font-medium hover:bg-electric-600 disabled:opacity-60 transition-colors"
                    >
                      <next.Icon className="w-3.5 h-3.5" />
                      {next.label}
                    </button>
                  )}
                  {o.status !== "cancelled" && o.status !== "delivered" && (
                    <button
                      onClick={() => updateStatus(o, "cancelled")}
                      disabled={busyId === o.id}
                      data-testid={`cancel-${o.order_number}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-border text-xs hover:border-destructive hover:text-destructive disabled:opacity-60 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-background rounded-2xl border border-border w-full max-w-2xl my-8">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-background/95 backdrop-blur-sm">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Order</div>
                <div className="font-heading font-medium text-2xl mt-1">{selected.order_number}</div>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full hover:bg-secondary flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Customer</div>
                  <div className="mt-1">{selected.shipping?.name}</div>
                  <div className="text-xs text-muted-foreground">{selected.email}</div>
                  <div className="text-xs text-muted-foreground">{selected.shipping?.phone}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Ship to</div>
                  <div className="mt-1 text-xs leading-relaxed">
                    {selected.shipping?.address_line1}<br />
                    {selected.shipping?.city}, {selected.shipping?.state} {selected.shipping?.pincode}<br />
                    {selected.shipping?.country}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-2">Items</div>
                <div className="space-y-2">
                  {selected.items?.map((it, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg border border-border">
                      <div className="w-12 h-12 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0 text-sm truncate">{it.name} × {it.quantity}</div>
                      <div className="text-sm tabular-nums">₹{(it.price * it.quantity).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="p-3 rounded-xl bg-secondary">
                  <div className="text-muted-foreground">Subtotal</div>
                  <div className="mt-1 font-medium tabular-nums">₹{selected.subtotal?.toLocaleString()}</div>
                </div>
                <div className="p-3 rounded-xl bg-secondary">
                  <div className="text-muted-foreground">Shipping</div>
                  <div className="mt-1 font-medium tabular-nums">₹{selected.shipping_fee}</div>
                </div>
                <div className="p-3 rounded-xl bg-electric/10">
                  <div className="text-electric">Total</div>
                  <div className="mt-1 font-medium tabular-nums">₹{selected.total?.toLocaleString()}</div>
                </div>
              </div>

              <div>
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-3">Timeline</div>
                <div className="space-y-3">
                  {selected.timeline?.map((t, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-electric mt-2 flex-shrink-0" />
                      <div>
                        <div>{t.text}</div>
                        <div className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {NEXT_STATUS[selected.status] && (
                <button
                  onClick={() => updateStatus(selected, NEXT_STATUS[selected.status].key)}
                  disabled={busyId === selected.id}
                  data-testid="modal-advance"
                  className="w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 disabled:opacity-60"
                >
                  {NEXT_STATUS[selected.status].label} <ChevronRight className="w-4 h-4 inline" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminTabs() {
  const loc = useLocation();
  const tabs = [
    { to: "/admin", label: "Products" },
    { to: "/admin/orders", label: "Orders" },
  ];
  return (
    <div className="mb-8 flex gap-1 p-1 rounded-full border border-border w-fit">
      {tabs.map((t) => {
        const active = loc.pathname === t.to;
        return (
          <Link
            key={t.to}
            to={t.to}
            data-testid={`admin-tab-${t.label.toLowerCase()}`}
            className={`h-9 px-5 inline-flex items-center rounded-full text-sm font-medium transition-colors ${
              active ? "bg-electric text-white" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </Link>
        );
      })}
    </div>
  );
}
